import { computed, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { MaskService } from 'src/app/shared/services/mask.service';
import {
  AppVisibilityStatus,
  ChargingStationLocationDisplay,
  CreateChargingStationLocation,
  CreateChargingStationLocationAddressForm,
  CreateChargingStationLocationExtraInformationForm,
  UpdateChargingStationLocationRequest,
} from '../charging-station-location-model';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Step2Form } from './steps/step2/step2.component';
import { FormGroup } from '@angular/forms';
import { ChargingStationLocationCreateModalForm } from './charging-station-location-create-edit-modal';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { normalizeFloorOrDoor, normalizeHouseLetter } from './steps/step2/danish-address-rules';

export interface ChargingStationLocationDialogData {
  locationIdentifier: string | null;
  organizationId?: number;
  departmentId: number;
  location: ChargingStationLocationDisplay;
}

@Injectable()
export class ChargingStationLocationCreatedEditDialogService {
  addressFoundAndSelected = signal(false);
  private maskService = inject(MaskService);
  geoLocationInputMask = this.maskService.geoLocationInputMask;
  organizationChanged = new BehaviorSubject<number | null>(null);
  readonly dialogData = inject<ChargingStationLocationDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  public powerPriceExclusionFeatureEnabled = signal(false);

  private featureFlagService = inject(FeatureFlagService);

  readonly data = computed(() => this.dialogData);
  readonly isEditing = computed(() => this.dialogData?.locationIdentifier != null);

  saving = signal(false);

  constructor() {
    this.featureFlagService
      .getFeatureFlag(FEATURE_FLAGS.POWER_PRICE_EXCLUSION)
      .pipe(
        takeUntilDestroyed(),
        tap(flagResult => this.powerPriceExclusionFeatureEnabled.set(flagResult.result))
      )
      .subscribe();
  }

  numericOnly(event: KeyboardEvent): boolean {
    const patt = /^([0-9])$/;
    const result = patt.test(event.key);
    return result;
  }

  mapAddress = (step2: FormGroup<Step2Form>) => {
    const address = step2.controls.address.value;

    let number = address.houseNumber?.toString();
    let letter = address.houseLetter?.toString();

    const match = number?.trim().match(/^(\d+)\s*([a-zA-ZæøåÆØÅ]+)$/);
    if (match) {
      number = match[1];
      letter = match[2];
    }
    const result = {
      houseNumber: Number(number),
      houseLetter: normalizeHouseLetter(letter),
      street: address.street?.trim(),
      town: address.town?.trim(),
      zipCode: address.zipCode?.trim(),
      floor: normalizeFloorOrDoor(address.floor),
      door: normalizeFloorOrDoor(address.door),
      darId: step2.controls.addressWithoutDARValidation.value ? null : (address.darId ?? null),
    } as CreateChargingStationLocationAddressForm;

    return result;
  };

  private mapMeteringPointIdentifiers(identifiers: string[]): string[] {
    return identifiers.map(identifier => `5713131${identifier}`);
  }

  private buildRelationInformation = (formInput: FormGroup<ChargingStationLocationCreateModalForm>) => {
    const form = formInput.getRawValue();

    const conceptInstallationNumber = form.step2.conceptInstallationNumber
      ? Number.parseInt(form.step2.conceptInstallationNumber, 10)
      : null;

    const extraInformation = {
      agreementNumber: form.step4.agreementNumber != null ? String(form.step4.agreementNumber) : null,
      meteringPointIdentifiers: this.mapMeteringPointIdentifiers(form.step2.meteringPointIdentifiers),
      hasOkPowerMeter: form.step2.hasOkPowerMeter ?? false,
      hasPowerPriceExclusion: form.step2.hasOkPowerMeter ? false : (form.step4.hasPowerPriceExclusion ?? false),
      category: form.step1.category,
      conceptInstallationNumber: Number.isNaN(conceptInstallationNumber as number) ? null : conceptInstallationNumber,
      locationAppVisibilityStatus: form.step2.locationAppVisibilityStatus,
      accessGroupIdentifiers: form.step3.selectedDefaultAccessGroups?.map(x => x.identifier),
      isLargeVehicleAccessible: form.step3.isLargeVehicleAccessible,
      appLocationText: form.step2.locationAppVisibilityStatus === AppVisibilityStatus.Restrictions ? form.step2.appLocationText : '',
      locationAppVisibility: form.step2.locationAppVisibility,
    } as CreateChargingStationLocationExtraInformationForm;

    return extraInformation;
  };

  public mapCreateRequest = (formInput: FormGroup<ChargingStationLocationCreateModalForm>) => {
    const relationInformaton = this.buildRelationInformation(formInput);

    const form = formInput.getRawValue();

    const request = {
      departmentId: form.step1.departmentId ? parseInt(form.step1.departmentId) : null,
      latitude: +(form.step2.latitude ?? '').toString().replace(',', '.'),
      longitude: +(form.step2.longitude ?? '').toString().replace(',', '.'),
      name: form.step2.name,
      organizationId: form.step1.organizationId,
      pricePolicyId: form.step3.pricePolicyId,
      RelationInfo: relationInformaton,
      address: this.mapAddress(formInput.controls.step2),
      restricted: form.step2.restricted,
      facilities: form.step3.facilities ?? [],
      kickbackId: form.step4.kickbackId,
    } as CreateChargingStationLocation;

    return request;
  };

  mapUpdateRequest = (
    formInput: FormGroup<ChargingStationLocationCreateModalForm>,
    pricePolicyChangeRequired: boolean
  ): UpdateChargingStationLocationRequest => {
    const form = formInput.getRawValue();
    const address = this.mapAddress(formInput.controls.step2);
    const hasOkPowerMeter = form.step2.hasOkPowerMeter ?? false;
    const conceptInstallationNumber = Number.parseInt(form.step2.conceptInstallationNumber ?? '', 10);

    return {
      address: {
        road: address.street,
        number: address.houseNumber,
        letter: address.houseLetter || null,
        floor: address.floor || null,
        door: address.door || null,
        postal_code: address.zipCode,
        city: address.town,
        latitude: +form.step2.latitude.replace(',', '.'),
        longitude: +form.step2.longitude.replace(',', '.'),
        dar_id: address.darId ?? null,
      },
      facilities: form.step3.facilities ?? [],
      // null ends the active product, so always send the current value
      product: form.step4.product ?? null,
      kickback_id: form.step4.kickbackId ?? null,
      relation: {
        kwh_compensation_agreement_number: form.step4.agreementNumber != null ? String(form.step4.agreementNumber) : null,
        restricted: form.step2.restricted,
        category: form.step1.category,
        concept_installation_number: Number.isNaN(conceptInstallationNumber) ? null : conceptInstallationNumber,
        app_visibility_status: form.step2.locationAppVisibilityStatus,
        location_app_visibility: form.step2.locationAppVisibility,
        app_location_text:
          form.step2.locationAppVisibilityStatus === AppVisibilityStatus.Restrictions ? (form.step2.appLocationText ?? '') : '',
        metering_point_identifiers: this.mapMeteringPointIdentifiers(form.step2.meteringPointIdentifiers),
        is_large_vehicle_accessible: form.step3.isLargeVehicleAccessible,
        has_ok_power_meter: hasOkPowerMeter,
        price_policy_id: pricePolicyChangeRequired ? form.step3.pricePolicyId : null,
        has_power_price_exclusion: hasOkPowerMeter ? false : (form.step4.hasPowerPriceExclusion ?? false),
      },
    };
  };
}
